import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, Loader2 } from 'lucide-react';
import { pushAPI } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';

// All possible notification types with i18n labels + short descriptions.
// Order + which set is shown depends on the current user's role.
const CLIENT_TYPES = [
  { key: 'booking_confirmed', es: 'Reserva confirmada', en: 'Booking confirmed', descEs: 'Cuando se confirma tu pago', descEn: 'When your payment is confirmed', locked: true },
  { key: 'booking_reminder_24h', es: 'Recordatorio 24h antes', en: '24h reminder', descEs: 'Un día antes de tu cita', descEn: 'One day before your booking' },
  { key: 'booking_reminder_1h', es: 'Recordatorio 1h antes', en: '1h reminder', descEs: 'Una hora antes de tu cita', descEn: 'One hour before your booking' },
  { key: 'booking_canceled_by_business', es: 'Negocio cancela tu cita', en: 'Business cancels your booking', descEs: 'Si el negocio cancela por alguna razón', descEn: 'If the business cancels for any reason', locked: true },
  { key: 'refund_processed', es: 'Reembolso procesado', en: 'Refund processed', descEs: 'Cuando llegue tu reembolso', descEn: "When your refund is on its way", locked: true },
  { key: 'review_request', es: 'Pedirte una reseña', en: 'Review request', descEs: 'Después del servicio', descEn: 'After your service' },
];

const BUSINESS_TYPES = [
  { key: 'new_booking', es: 'Nueva reserva', en: 'New booking', descEs: 'Un cliente reservó y pagó', descEn: 'A customer booked and paid', locked: true },
  { key: 'booking_canceled_by_customer', es: 'Cliente cancela', en: 'Customer cancels', descEs: 'Cuando un cliente cancela', descEn: 'When a customer cancels', locked: true },
  { key: 'settlement_sent', es: 'Liquidación mensual', en: 'Monthly settlement', descEs: 'Cuando te enviamos tus anticipos', descEn: 'When we send you your deposits', locked: true },
  { key: 'business_approved', es: 'Tu negocio fue aprobado', en: 'Your business was approved', descEs: 'Cuando pasas el KYC', descEn: 'When you pass KYC', locked: true },
  { key: 'subscription_expired', es: 'Suscripción vencida', en: 'Subscription expired', descEs: 'Si tu suscripción caduca', descEn: 'If your subscription expires', locked: true },
  { key: 'trial_ending', es: 'Tu prueba termina pronto', en: 'Your trial is ending', descEs: '3 días antes de terminar', descEn: '3 days before it ends' },
];

/**
 * Renders the notification preferences panel.
 * `variant`:
 *   - "client" → only CLIENT_TYPES
 *   - "business" → only BUSINESS_TYPES
 *   - "both" → both groups (rare — only for users who are both)
 * `locked` types can't be turned off (critical transactional notifications).
 */
export default function NotificationPreferencesCard({ variant = 'client' }) {
  const { language } = useI18n();
  const es = language === 'es';
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await pushAPI.getPreferences();
        if (!cancelled) setPrefs(data.preferences || {});
      } catch {
        if (!cancelled) setPrefs({});
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = async (key, next) => {
    setSaving(s => ({ ...s, [key]: true }));
    const previous = prefs[key];
    // Optimistic update
    setPrefs(p => ({ ...p, [key]: next }));
    try {
      await pushAPI.updatePreferences({ [key]: next });
      toast.success(es ? 'Preferencia actualizada' : 'Preference updated');
    } catch {
      setPrefs(p => ({ ...p, [key]: previous }));
      toast.error(es ? 'No se pudo actualizar' : "Couldn't update");
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const groups = [];
  if (variant === 'client' || variant === 'both') {
    groups.push({ title: es ? 'Como cliente' : 'As a client', items: CLIENT_TYPES });
  }
  if (variant === 'business' || variant === 'both') {
    groups.push({ title: es ? 'Como negocio' : 'As a business', items: BUSINESS_TYPES });
  }

  return (
    <Card data-testid="notification-preferences-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading">
          <Bell className="h-5 w-5 text-[#F05D5E]" />
          {es ? 'Notificaciones push' : 'Push notifications'}
        </CardTitle>
        <CardDescription>
          {es
            ? 'Elige qué avisos quieres recibir en tu teléfono. Los avisos críticos (pago, cancelación, reembolso) no se pueden desactivar.'
            : "Pick which alerts you want on your phone. Critical alerts (payment, cancellation, refund) can't be turned off."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        )}
        {!loading && groups.map(group => (
          <div key={group.title} className="space-y-1">
            {groups.length > 1 && (
              <p className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wide mb-2">
                {group.title}
              </p>
            )}
            {group.items.map(item => {
              const value = prefs?.[item.key] !== false; // default true
              const isSaving = !!saving[item.key];
              const isLocked = !!item.locked;
              return (
                <div
                  key={item.key}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg border border-border/60 ${
                    isLocked ? 'bg-muted/30' : ''
                  }`}
                  data-testid={`pref-row-${item.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium flex items-center gap-1.5">
                      {es ? item.es : item.en}
                      {isLocked && (
                        <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          {es ? 'Crítico' : 'Critical'}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {es ? item.descEs : item.descEn}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                    <Switch
                      checked={isLocked ? true : value}
                      disabled={isLocked || isSaving}
                      onCheckedChange={(v) => handleToggle(item.key, v)}
                      data-testid={`pref-toggle-${item.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
